/**
 * Выводы классических индикаторов словами.
 *
 * Каждая функция получает уже посчитанные ряды — ровно те, что нарисованы
 * на графике, — и отвечает тем, что сказал бы опытный трейдер, глядя на
 * них: что сейчас, насколько это заметно и чего это не значит. Последнее
 * важно не меньше первого: «RSI выше 70» новичок читает как «продавай»,
 * и табло обязано сказать, что это не так.
 *
 * Выводы — по последней закрытой свече: формирующаяся ещё передумает.
 */

import type { Candle } from '@specter/types';
import type { IndicatorShape, ReadoutRow } from './overlays.js';
import {
  closedIndex,
  crossesOf,
  markAt,
  meanOf,
  percentWords,
  priceWords,
  rowTone,
  situationRows,
  slopeOf,
  timesWords,
  whenWords,
  type Situation,
} from './explain.js';

type Series = readonly (number | null)[];

export interface Explained {
  readonly rows: readonly ReadoutRow[];
  readonly marks: readonly IndicatorShape[];
}

const NOTHING: Explained = { rows: [], marks: [] };

function at(values: Series, index: number): number | null {
  return index >= 0 ? (values[index] ?? null) : null;
}

/* ------------------------------------------------------ скользящая средняя */

/** Наклон меньше этого за пять свечей — средняя идёт ровно. */
const FLAT_SLOPE = 0.001;

export function explainAverage(candles: readonly Candle[], line: Series, livePartial: boolean): Explained {
  const i = closedIndex(candles, livePartial);
  const value = at(line, i);
  const candle = candles[i];
  if (value === null || !candle) return NOTHING;

  const slope = slopeOf(line, i, 5) ?? 0;
  const rising = slope > FLAT_SLOPE;
  const falling = slope < -FLAT_SLOPE;
  const above = candle.close > value;
  const distance = ((candle.close - value) / value) * 100;

  let situation: Situation;
  if (above && rising) {
    situation = {
      title: 'Тренд вверх',
      tone: 'bull',
      text: 'Цена выше средней, и сама средняя растёт. При откатах средняя часто служит опорой.',
    };
  } else if (!above && falling) {
    situation = {
      title: 'Тренд вниз',
      tone: 'bear',
      text: 'Цена ниже средней, и сама средняя падает. При отскоках средняя часто служит потолком.',
    };
  } else if (above && falling) {
    situation = {
      title: 'Попытка разворота вверх',
      tone: 'warn',
      text: 'Цена поднялась над падающей средней. Разворот возможен, но пока средняя смотрит вниз, тренд не сменился.',
    };
  } else if (!above && rising) {
    situation = {
      title: 'Откат в росте',
      tone: 'warn',
      text: 'Цена опустилась под растущую среднюю. Это может быть откат в восходящем тренде или начало разворота — смотрите, вернётся ли цена выше.',
    };
  } else {
    situation = {
      title: 'Без тренда',
      tone: 'muted',
      text: 'Средняя идёт ровно, цена ходит вокруг неё. Ясного направления нет, и пересечения здесь часто ложные.',
    };
  }

  return {
    rows: [
      ...situationRows(situation),
      { label: 'Цена от средней', value: percentWords(distance, 2), tone: above ? 'up' : 'down' },
      { label: 'Средняя', value: rising ? 'растёт' : falling ? 'падает' : 'идёт ровно', tone: rising ? 'up' : falling ? 'down' : 'muted' },
    ],
    marks: [],
  };
}

/* ---------------------------------------------------------- три средних */

export function explainTriple(
  candles: readonly Candle[],
  fast: Series,
  mid: Series,
  slow: Series,
  livePartial: boolean,
): Explained {
  const i = closedIndex(candles, livePartial);
  const f = at(fast, i);
  const m = at(mid, i);
  const s = at(slow, i);
  if (f === null || m === null || s === null) return NOTHING;

  let situation: Situation;
  if (f > m && m > s) {
    situation = {
      title: 'Тренд вверх',
      tone: 'bull',
      text: 'Короткая выше средней, средняя выше длинной — рост во всех масштабах. Пока порядок держится, тренд жив.',
    };
  } else if (f < m && m < s) {
    situation = {
      title: 'Тренд вниз',
      tone: 'bear',
      text: 'Короткая ниже средней, средняя ниже длинной — снижение во всех масштабах. Пока порядок держится, тренд жив.',
    };
  } else {
    situation = {
      title: 'Средние переплетены',
      tone: 'muted',
      text: 'Средние перепутаны — у рынка нет ясного направления. Пересечения в такое время часто ложные.',
    };
  }

  /*
   * Отметки — пересечения короткой с длинной: это смена направления
   * целиком, а не дрожь соседних линий. Пересечения соседних шли бы
   * через свечу и превратили бы график в частокол.
   */
  const crosses = crossesOf(fast, slow, i, 5);
  const marks = crosses.map((cross) =>
    markAt(candles[cross.index]!, cross.up, cross.up ? 'пересечение вверх' : 'пересечение вниз'),
  );
  const lastCross = crosses.at(-1);

  const rows: ReadoutRow[] = [...situationRows(situation)];
  if (lastCross) {
    rows.push({
      label: 'Последнее пересечение',
      value: `${whenWords(candles[lastCross.index]!.time)} · ${lastCross.up ? 'вверх' : 'вниз'}`,
      tone: lastCross.up ? 'up' : 'down',
    });
  }
  return { rows, marks };
}

/* -------------------------------------------------------------- Боллинджер */

export function explainBollinger(
  candles: readonly Candle[],
  upper: Series,
  middle: Series,
  lower: Series,
  livePartial: boolean,
): Explained {
  const i = closedIndex(candles, livePartial);
  const u = at(upper, i);
  const mi = at(middle, i);
  const l = at(lower, i);
  const candle = candles[i];
  if (u === null || mi === null || l === null || !candle || u <= l || mi === 0) return NOTHING;

  const position = (candle.close - l) / (u - l);
  const width = (u - l) / mi;

  // Ширина против обычной: медиана за последние сто двадцать свечей.
  const widths: number[] = [];
  for (let j = Math.max(0, i - 120); j <= i; j += 1) {
    const a = upper[j];
    const b = lower[j];
    const c = middle[j];
    if (a == null || b == null || c == null || c === 0) continue;
    widths.push((a - b) / c);
  }
  const sorted = [...widths].sort((a, b) => a - b);
  const usual = sorted[Math.floor(sorted.length / 2)] ?? width;
  const rank = sorted.filter((value) => value < width).length / Math.max(1, sorted.length);
  const squeeze = sorted.length >= 40 && rank <= 0.15;

  let situation: Situation;
  if (position > 1) {
    situation = {
      title: 'Выше верхней полосы',
      tone: 'warn',
      text: 'Цена вышла за верхнюю полосу — движение резкое. В сильном тренде так бывает подолгу, во флэте цена обычно возвращается внутрь.',
    };
  } else if (position < 0) {
    situation = {
      title: 'Ниже нижней полосы',
      tone: 'warn',
      text: 'Цена вышла за нижнюю полосу — падение резкое. В сильном тренде так бывает подолгу, во флэте цена обычно возвращается внутрь.',
    };
  } else if (squeeze) {
    situation = {
      title: 'Сжатие',
      tone: 'warn',
      text: 'Полосы сжались сильнее обычного — рынок копит силы, скоро вероятно резкое движение. В какую сторону, полосы не подскажут.',
    };
  } else if (position >= 0.8) {
    situation = { title: 'У верхней полосы', tone: 'bull', text: 'Цена прижата к верхней полосе — покупатели сильнее. Средняя линия — ближайшая опора при откате.' };
  } else if (position <= 0.2) {
    situation = { title: 'У нижней полосы', tone: 'bear', text: 'Цена прижата к нижней полосе — продавцы сильнее. Средняя линия — ближайший потолок при отскоке.' };
  } else {
    situation = { title: 'Внутри полос', tone: 'muted', text: 'Цена в середине полос — обычное движение без крайностей.' };
  }

  return {
    rows: [
      ...situationRows(situation),
      { label: 'Место в полосах', value: `${Math.round(position * 100)}%`, meter: Math.min(100, Math.max(0, position * 100)) },
      {
        label: 'Ширина полос',
        value: `${timesWords(width / usual)} от обычной`,
        tone: width / usual < 0.8 ? 'muted' : undefined,
      },
    ],
    marks: [],
  };
}

/* --------------------------------------------------------------------- RSI */

export function explainRsi(candles: readonly Candle[], rsi: Series, livePartial: boolean): Explained {
  const i = closedIndex(candles, livePartial);
  const value = at(rsi, i);
  if (value === null) return NOTHING;

  let situation: Situation;
  if (value >= 70) {
    situation = {
      title: 'Перекупленность',
      tone: 'warn',
      text: 'RSI выше 70 — рост был резким. Это не сигнал продавать: в сильном тренде RSI держится наверху долго. Признак разворота — возврат ниже 70.',
    };
  } else if (value <= 30) {
    situation = {
      title: 'Перепроданность',
      tone: 'warn',
      text: 'RSI ниже 30 — падение было резким. Это не сигнал покупать: в сильном тренде RSI держится внизу долго. Признак разворота — возврат выше 30.',
    };
  } else if (value >= 50) {
    situation = { title: 'Покупатели сильнее', tone: 'bull', text: 'RSI выше 50 — последние свечи больше росли, чем падали.' };
  } else {
    situation = { title: 'Продавцы сильнее', tone: 'bear', text: 'RSI ниже 50 — последние свечи больше падали, чем росли.' };
  }

  /*
   * Отметки — выходы из крайних зон, а не вход в них: вход в
   * перекупленность в тренде значит только силу, а выход обратно —
   * первый признак, что сила кончилась.
   */
  const marks: IndicatorShape[] = [];
  for (let j = 1; j <= i; j += 1) {
    const before = rsi[j - 1];
    const now = rsi[j];
    if (before == null || now == null) continue;
    if (before >= 70 && now < 70) marks.push(markAt(candles[j]!, false, 'выход из перекупленности'));
    if (before <= 30 && now > 30) marks.push(markAt(candles[j]!, true, 'выход из перепроданности'));
  }

  return {
    rows: [...situationRows(situation), { label: 'RSI', value: value.toFixed(1).replace('.', ','), meter: value, tone: rowTone(situation.tone) }],
    marks,
  };
}

/* -------------------------------------------------------------------- MACD */

export function explainMacd(
  candles: readonly Candle[],
  macd: Series,
  signal: Series,
  histogram: Series,
  livePartial: boolean,
): Explained {
  const i = closedIndex(candles, livePartial);
  const m = at(macd, i);
  const s = at(signal, i);
  const h = at(histogram, i);
  const hBefore = at(histogram, i - 1);
  if (m === null || s === null || h === null || hBefore === null) return NOTHING;

  const growing = Math.abs(h) > Math.abs(hBefore);
  let situation: Situation;
  if (h > 0 && growing) {
    situation = { title: 'Импульс вверх растёт', tone: 'bull', text: 'MACD выше сигнальной, и разрыв увеличивается — движение вверх набирает силу.' };
  } else if (h > 0) {
    situation = {
      title: 'Импульс вверх слабеет',
      tone: 'warn',
      text: 'MACD ещё выше сигнальной, но разрыв сокращается — рост теряет силу, скоро возможно пересечение вниз.',
    };
  } else if (h < 0 && growing) {
    situation = { title: 'Импульс вниз растёт', tone: 'bear', text: 'MACD ниже сигнальной, и разрыв увеличивается — движение вниз набирает силу.' };
  } else {
    situation = {
      title: 'Импульс вниз слабеет',
      tone: 'warn',
      text: 'MACD ещё ниже сигнальной, но разрыв сокращается — падение теряет силу, скоро возможно пересечение вверх.',
    };
  }

  /*
   * Отметки — только сильные пересечения: вверх под нулём и вниз над
   * нулём. Разворот против общего тренда стоит внимания; пересечение по
   * тренду случается на каждом откате и ничего не добавляет.
   */
  const marks = crossesOf(macd, signal, i, 3)
    .filter((cross) => {
      const level = macd[cross.index];
      return level != null && (cross.up ? level < 0 : level > 0);
    })
    .map((cross) => markAt(candles[cross.index]!, cross.up, cross.up ? 'MACD вверх' : 'MACD вниз'));

  return {
    rows: [
      ...situationRows(situation),
      {
        label: 'Общий тренд',
        value: m > 0 ? 'выше нуля — вверх' : 'ниже нуля — вниз',
        tone: m > 0 ? 'up' : 'down',
      },
    ],
    marks,
  };
}

/* --------------------------------------------------------------------- ATR */

export function explainAtr(candles: readonly Candle[], atr: Series, livePartial: boolean): Explained {
  const i = closedIndex(candles, livePartial);
  const value = at(atr, i);
  const candle = candles[i];
  const usual = meanOf(atr, i, 100);
  if (value === null || !candle || usual === null || usual <= 0) return NOTHING;

  const ratio = value / usual;
  let situation: Situation;
  if (ratio >= 1.3) {
    situation = {
      title: 'Рынок разогнан',
      tone: 'warn',
      text: `Свечи шире обычного в ${ratio.toFixed(1).replace('.', ',')} раза. Стоп нужно ставить дальше, а позицию делать меньше.`,
    };
  } else if (ratio <= 0.75) {
    situation = {
      title: 'Рынок затих',
      tone: 'muted',
      text: 'Свечи уже обычного. Затишье на рынке часто сменяется резким движением.',
    };
  } else {
    situation = { title: 'Обычная волатильность', tone: 'muted', text: 'Размах свечей обычный для этой монеты.' };
  }

  return {
    rows: [
      ...situationRows(situation),
      { label: 'Обычный ход свечи', value: `${priceWords(value)} (${percentWords((value / candle.close) * 100, 2).replace('+', '')})` },
      // Полтора ATR — привычное расстояние, на котором стоп не выбивает шум.
      { label: 'Стоп не ближе', value: priceWords(value * 1.5), tone: 'muted' },
    ],
    marks: [],
  };
}

/* ------------------------------------------------------------------- объём */

export function explainVolume(candles: readonly Candle[], livePartial: boolean, period = 20): Explained {
  const i = closedIndex(candles, livePartial);
  const candle = candles[i];
  if (!candle || i < period) return NOTHING;
  const usual = meanOf(
    candles.map((item) => item.volume),
    i - 1,
    period,
  );
  if (usual === null || usual <= 0) return NOTHING;

  const ratio = candle.volume / usual;
  const up = candle.close >= candle.open;
  let situation: Situation;
  if (ratio >= 2) {
    situation = up
      ? { title: 'Всплеск на росте', tone: 'bull', text: 'Объём вдвое выше обычного на растущей свече — за движением стоят деньги.' }
      : { title: 'Всплеск на падении', tone: 'bear', text: 'Объём вдвое выше обычного на падающей свече — продают всерьёз.' };
  } else if (ratio <= 0.5) {
    situation = {
      title: 'Вялая торговля',
      tone: 'muted',
      text: 'Объём вдвое ниже обычного. Движение без объёма легко разворачивается — не стоит доверять ему много.',
    };
  } else {
    situation = { title: 'Обычный объём', tone: 'muted', text: 'Торгуют как обычно: ни всплеска, ни затишья.' };
  }

  return {
    rows: [...situationRows(situation), { label: 'Закрытая свеча', value: `${timesWords(ratio)} от обычного`, tone: rowTone(situation.tone) }],
    marks: [],
  };
}
